import {
    mat3,
    ReadonlyMat3,
    ReadonlyVec2,
    ReadonlyVec3,
    vec2,
    vec3,
} from 'gl-matrix';
import { BoundingBox } from '../common/BoundingBox';
import { ContourSlice } from './ContourSlice';
import { ContourTexture } from './ContourTexture';
import { Contour } from './Contour';
import { marchingSquares, simplify } from '~/engine/core/utils/marchingSquares';
import { Grid } from '../common/Grid';

/**
 * {@link ContourSequence} stores a collection of {@link ContourSlice ContourSlices}.\
 * {@link ContourSequence} stores the contour data in the form of planar connected points.
 */
export class ContourSequence {
    /**
     * Number of points stored in this {@link ContourSequence}.
     */
    private totalVertexCount: number;

    /**
     * Centroid point calculated as the average from all the vertices in this {@link ContourSequence}.
     */
    private _centroid?: vec3;

    /**
     * Normal direction of this {@link ContourSequence}.\
     * This should be the direction in which the {@link ContourSlice ContourSlices} are stacked.\
     * It is computed from the first {@link ContourSlice} and {@link vec3.negate negated} if the {@link ContourSlice ContourSlices} are stacked in opposing direction.
     */
    private readonly _normalDirection: ReadonlyVec3;

    private _boundingBox?: BoundingBox;

    public contour?: vec2[];
    public reducedContour?: vec2[];
    public sdfDebug?: Uint8Array;
    private _contourSlices: ContourSlice[];

    public constructor(
        private readonly _imageGrid: Grid,
        contourSlices: ContourSlice[]
    ) {
        this._normalDirection = _imageGrid.getUnitZ();
        this._contourSlices = contourSlices
            .slice()
            .sort((a: ContourSlice, b: ContourSlice): number => {
                return a.normalPosition - b.normalPosition;
            });
        this.correctWinding();
        this.totalVertexCount = this._contourSlices.reduce(
            (sum, cur) => sum + cur.vertexCount,
            0
        );
    }

    public get contourSlices(): ContourSlice[] {
        return this._contourSlices;
    }

    public get lowestSlice(): ContourSlice {
        return this._contourSlices[0];
    }

    public get highestSlice(): ContourSlice {
        return this._contourSlices[this._contourSlices.length - 1];
    }

    public normalPositionToSlice(normalPosition: number) {
        return this._contourSlices.find((slice) =>
            ContourSlice.isSameSlice(slice.normalPosition, normalPosition)
        );
    }

    public get vertexCount(): number {
        return this.totalVertexCount;
    }

    public get numberOfSlices(): number {
        return this._contourSlices.length;
    }

    public get normalDirection(): ReadonlyVec3 {
        return this._normalDirection;
    }

    public get centroid(): ReadonlyVec3 {
        if (!this._centroid) {
            this._centroid = vec3.create();
            for (const slice of this._contourSlices) {
                vec3.scaleAndAdd(
                    this._centroid,
                    this._centroid,
                    slice.centroid,
                    slice.vertexCount / this.vertexCount
                );
            }
        }
        return this._centroid;
    }

    public get rotation(): ReadonlyMat3 {
        return mat3.create();
    }

    public get center(): ReadonlyVec3 {
        return this.boundingBox.center;
    }

    public get widthMm(): number {
        return this.boundingBox.size[0];
    }

    public get heightMm(): number {
        return this.boundingBox.size[1];
    }

    public generateVertex(
        _x: 1 | -1,
        _y: 1 | -1,
        _normalPosition: number
    ): vec3 {
        throw new Error('Method not implemented.');
    }

    public addSlice(slice: ContourSlice) {
        this.totalVertexCount += slice.vertexCount;
        this._centroid = undefined;
        this._contourSlices.push(slice);
        this._contourSlices.sort((a: ContourSlice, b: ContourSlice): number => {
            return a.normalPosition - b.normalPosition;
        });
    }

    private correctWinding(): void {
        const rotation = this._imageGrid.getOrientation();
        for (const s of this._contourSlices) {
            if (s.numberOfContours > 1) {
                //separate holes from filled contours
                const planarVertices: ReadonlyVec2[][] = [];
                for (const c of s.contours) {
                    planarVertices.push(
                        c.correctWinding(rotation as ReadonlyMat3)
                    );
                }
                for (let i = 0; i < planarVertices.length; ++i) {
                    let overlaps = 0;
                    const ref = planarVertices[i][0];
                    for (let j = 0; j < planarVertices.length; ++j) {
                        if (i === j) {
                            //don't check overlaps with itself
                            continue;
                        }
                        let previousX: number = planarVertices[j][0][0];
                        let previousSign = Math.sign(
                            planarVertices[j][0][1] - ref[1]
                        );
                        const n = planarVertices[j].length;
                        for (let k = 1; k < n; ++k) {
                            const x = planarVertices[j][k][0];
                            const sign = Math.sign(
                                planarVertices[j][k][1] - ref[1]
                            );
                            if (
                                x <= ref[0] &&
                                previousX <= ref[0] &&
                                sign !== previousSign
                            ) {
                                ++overlaps;
                            }
                            previousX = x;
                            previousSign = sign;
                        }
                    }
                    if (overlaps & 1) {
                        //odd number of overlaps
                        const contour = s.getContour(i);
                        if (contour) {
                            contour.reverseWinding();
                        } else {
                            throw new Error(
                                `Attempt to reverse the winding of contour index ${i}, while slice only has ${s.numberOfContours} contour(s).`
                            );
                        }
                    }
                }
            } else {
                for (const c of s.contours) {
                    c.correctWinding(rotation);
                }
            }
        }
    }

    /**
     * Method to get a bounding box that covers all the ContourPoints.
     */
    public get boundingBox(): BoundingBox {
        if (!this._boundingBox) {
            const minPoint = vec3.fromValues(
                Number.MAX_VALUE,
                Number.MAX_VALUE,
                Number.MAX_VALUE
            );
            const maxPoint = vec3.fromValues(
                Number.MIN_VALUE,
                Number.MIN_VALUE,
                Number.MIN_VALUE
            );
            for (const slice of this._contourSlices) {
                for (const contour of slice.contours) {
                    for (const vertex of contour) {
                        vec3.min(minPoint, minPoint, vertex);
                        vec3.max(maxPoint, maxPoint, vertex);
                    }
                }
            }
            if (minPoint[0] > maxPoint[0]) {
                //no points
                minPoint[0] = minPoint[1] = minPoint[2] = 0;
                maxPoint[0] = maxPoint[1] = maxPoint[2] = 0;
            }
            this._boundingBox = new BoundingBox(minPoint, maxPoint);
        }

        return this._boundingBox;
    }

    public addSlices(...slices: ContourSlice[]): ContourSequence {
        for (const slice of slices) {
            this.addSlice(slice);
            this.totalVertexCount += slice.vertexCount;
        }
        this._centroid = undefined;
        return this;
    }

    public updateFromContourTexture(texture: ContourTexture) {
        if (texture.signedDistanceField) {
            const sdfArray = texture.signedDistanceField.toArray();
            this.sdfDebug = sdfArray[Math.floor(sdfArray.length / 2)];
            const xsize = texture.signedDistanceField.size[0];
            const ysize = texture.signedDistanceField.size[1];

            // the contour threshold value is set to half of the maximum SDF value
            let maxSdfVal = 0;

            for (const sdfSlice of sdfArray) {
                for (let i = 0; i < sdfSlice.length; i++) {
                    if (sdfSlice[i] > maxSdfVal) {
                        maxSdfVal = sdfSlice[i];
                    }
                }
            }
            let maxLen = 0;

            let contourSliceIdx = 0;
            const textureRotation = texture.rotation;
            const resolution = texture.xyResolution;
            const textureOffset = vec3.sub(
                vec3.create(),
                texture.center,
                vec3.transformMat3(
                    vec3.create(),
                    vec3.fromValues(
                        texture.widthMm / 2 - 0.5 * resolution,
                        texture.heightMm / 2 - 0.5 * resolution,
                        0
                    ),
                    textureRotation
                )
            );
            textureOffset[2] = 0;
            for (const sdfSlice of sdfArray) {
                const contours2d = marchingSquares(
                    sdfSlice,
                    xsize,
                    ysize,
                    1,
                    1,
                    maxSdfVal / 2
                );
                const slicePosition =
                    texture.sliceIndexToNormalPosition(contourSliceIdx);

                const contours3d = contours2d.map((c) => {
                    const reducedContour = simplify(c, 0.1, true);

                    if (c.length > maxLen) {
                        this.contour = c;
                        maxLen = c.length;
                        this.reducedContour = reducedContour;
                    }

                    return new Contour(
                        reducedContour.map((p) =>
                            vec3.add(
                                vec3.create(),
                                textureOffset,
                                vec3.transformMat3(
                                    vec3.create(),
                                    vec3.fromValues(
                                        p[0] * resolution,
                                        p[1] * resolution,
                                        slicePosition
                                    ),
                                    textureRotation
                                )
                            )
                        )
                    );
                });

                const slice = this.normalPositionToSlice(slicePosition);
                if (slice) {
                    slice.contours = contours3d;
                } else if (contours3d.length > 0) {
                    this.addSlice(new ContourSlice(slicePosition, contours3d));
                }
                contourSliceIdx++;
            }
        }
        this._centroid = undefined;
        this._boundingBox = undefined;
    }
}
